// src/components/Attendance/MonthlyReportSection.js (Corrected for PDF Export)

import React, { useState, useEffect, useRef } from 'react';
import { useAppContext } from '../../App';
import { api } from '../../apiService';
import { toast } from 'react-toastify';

const getEmployeeKey = (emp) => emp?.employeeId || emp?.id;

function MonthlyReportSection() {
    const { currentStationId } = useAppContext();
    const [employees, setEmployees] = useState([]);
    const [reportData, setReportData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const reportRef = useRef(null);

    useEffect(() => {
        if (!currentStationId || currentStationId === 'all') {
            setEmployees([]);
            return;
        }
        const fetchEmployees = async () => {
            try {
                const data = await api.get('workers', { where: { stationId: currentStationId }, orderBy: 'fullName' });
                setEmployees(data);
            } catch (err) {
                console.error("Error fetching employees for report: ", err);
                toast.error("Could not fetch employee list for reports.");
            }
        };
        fetchEmployees();
    }, [currentStationId]);
    
    const toYYYYMMDD = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const handleGenerateReport = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setReportData(null);

        const form = e.target;
        const employeeId = form.elements['employee-select'].value;
        const month = parseInt(form.elements['month-select'].value);
        const year = parseInt(form.elements['year-select'].value);

        if (!employeeId) {
            toast.error("Please select an employee.");
            setIsLoading(false);
            return;
        }
        const employee = employees.find(emp => String(getEmployeeKey(emp)) === String(employeeId));
        if (!employee) {
            toast.error("Selected employee not found.");
            setIsLoading(false);
            return;
        }

        try {
            const startDateStr = `${year}-${String(month + 1).padStart(2, '0')}-01`;
            const endDate = new Date(year, month + 1, 0);
            const endDateStr = toYYYYMMDD(endDate);
            
            const employeeIdKeys = Array.from(new Set([employee.employeeId, employee.id, employeeId].filter(Boolean)));
            const employeeIdWhere = employeeIdKeys.length > 1 ? { in: employeeIdKeys } : employeeIdKeys[0];
            const stationId = employee.stationId;
            const [attendanceByDate, attendanceByCheckIn] = await Promise.all([
                api.get('attendance', {
                    where: {
                        stationId,
                        employeeId: employeeIdWhere,
                        date: { '>=': startDateStr, '<=': endDateStr }
                    }
                }).catch(() => []),
                api.get('attendance', {
                    where: {
                        stationId,
                        employeeId: employeeIdWhere,
                        checkIn: { '>=': `${startDateStr} 00:00:00`, '<=': `${endDateStr} 23:59:59` }
                    }
                }).catch(() => [])
            ]);
            const byId = new Map();
            [...attendanceByDate, ...attendanceByCheckIn].forEach((log) => {
                if (!log) return;
                const key = log.id !== undefined && log.id !== null ? String(log.id) : JSON.stringify(log);
                if (!byId.has(key)) byId.set(key, log);
            });
            const attendanceLogs = Array.from(byId.values());

            const daysInMonth = endDate.getDate();
            const todayKey = toYYYYMMDD(new Date());

            const toDayKey = (value) => {
                if (!value) return '';
                if (typeof value === 'string') return value.trim().slice(0, 10);
                if (value instanceof Date && !Number.isNaN(value.getTime())) return value.toISOString().slice(0, 10);
                const d = new Date(value);
                if (Number.isNaN(d.getTime())) return '';
                return d.toISOString().slice(0, 10);
            };
            const toDayKeyPk = (value) => {
                const d = value instanceof Date ? value : new Date(value);
                if (Number.isNaN(d.getTime())) return '';
                return d.toLocaleDateString('en-CA', { timeZone: 'Asia/Karachi' });
            };

            const getLogDayKey = (log) => {
                const dateKey = toDayKey(log?.date);
                if (dateKey) return dateKey;
                return toDayKeyPk(log?.checkIn);
            };
            
            const logsByDay = new Map();
            (attendanceLogs || []).forEach((log) => {
                const key = getLogDayKey(log);
                if (!key) return;
                const existing = logsByDay.get(key);
                if (!existing) {
                    logsByDay.set(key, log);
                    return;
                }
                const exTime = new Date(existing.checkIn || existing.createdAt || 0).getTime();
                const curTime = new Date(log.checkIn || log.createdAt || 0).getTime();
                if (curTime > exTime) logsByDay.set(key, log);
            });
            
            const fullMonthData = Array.from({ length: daysInMonth }, (_, i) => {
                const currentDate = new Date(year, month, i + 1);
                const dateStr = toYYYYMMDD(currentDate);
                const logForDay = logsByDay.get(dateStr);

                const locationData = {
                    name: logForDay?.checkInLocation?.name || '--',
                    lat: logForDay?.checkInLocation?.latitude,
                    lon: logForDay?.checkInLocation?.longitude,
                };
                
                const entry = {
                    date: dateStr,
                    checkIn: logForDay?.checkIn ? new Date(logForDay.checkIn).toLocaleTimeString() : '--',
                    checkOut: logForDay?.checkOut ? new Date(logForDay.checkOut).toLocaleTimeString() : '--',
                    location: locationData,
                    status: 'Scheduled',
                };

                if (logForDay) {
                    entry.status = logForDay.status;
                } else if (dateStr <= todayKey) {
                    entry.status = 'Absent';
                }

                return entry;
            });

            const summary = fullMonthData.reduce((acc, day) => {
                if (['Present', 'Late', 'Completed'].includes(day.status)) acc.present++;
                if (day.status === 'Late') acc.late++;
                if (day.status === 'Absent') acc.absent++;
                if (day.status === 'On Leave') acc.onLeave++;
                return acc;
            }, { present: 0, absent: 0, late: 0, onLeave: 0 });

            setReportData({ employee, period: `${new Date(year, month).toLocaleString('default', { month: 'long' })} ${year}`, logs: fullMonthData, summary });
        } catch (error) {
            console.error("Error generating report:", error);
            toast.error("Failed to generate report.");
        } finally {
            setIsLoading(false);
        }
    };

    // --- START: CORRECTED EXPORT FUNCTION ---
    const handleExport = () => {
        const { jsPDF } = window.jspdf;
        const input = reportRef.current;
        if (input) {
            // Find ONLY the button to hide it
            const buttonToHide = input.querySelector('.export-pdf-button');
            if (buttonToHide) buttonToHide.style.display = 'none';

            window.html2canvas(input, { scale: 2 }).then(canvas => {
                const imgData = canvas.toDataURL('image/png');
                const pdf = new jsPDF('p', 'mm', 'a4');
                const pdfWidth = pdf.internal.pageSize.getWidth();
                const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
                pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
                pdf.save(`Attendance_Report_${reportData.employee.fullName}_${reportData.period}.pdf`);
                
                // Show the button again after export
                if (buttonToHide) buttonToHide.style.display = 'block';
            });
        }
    };
    // --- END: CORRECTED EXPORT FUNCTION ---
    
    const getStatusStyles = (status) => {
        switch (status) {
            case 'Present':
            case 'Completed':
                return 'bg-green-100 text-green-800';
            case 'Late':
                return 'bg-yellow-100 text-yellow-800';
            case 'Absent':
                return 'bg-red-100 text-red-800';
            case 'On Leave':
                return 'bg-blue-100 text-blue-800';
            case 'Scheduled':
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    return (
        <div className="container mx-auto">
            <div className="mb-8">
                <h1 className="text-4xl font-bold text-gray-800">Employee Attendance Report</h1>
                <p className="text-gray-500 mt-1">Generate and export monthly attendance reports.</p>
            </div>
            
            <div className="bg-white p-6 rounded-2xl shadow-lg mb-8">
                <form onSubmit={handleGenerateReport} className="grid grid-cols-1 md:grid-cols-6 gap-4 items-end">
                    <div className="md:col-span-3">
                        <label htmlFor="employee-select" className="block text-sm font-medium text-gray-600">Select Employee</label>
                        <select id="employee-select" className="mt-1 block w-full bg-gray-50 border-gray-300 rounded-md shadow-sm p-3 font-semibold" required>
                             <option value="">-- Select --</option>
                             {employees.map(emp => <option key={getEmployeeKey(emp)} value={getEmployeeKey(emp)}>{emp.fullName} ({emp.employeeId || emp.id})</option>)}
                        </select>
                    </div>
                     <div className="md:col-span-1">
                        <label htmlFor="month-select" className="block text-sm font-medium text-gray-600">Month</label>
                        <select id="month-select" defaultValue={new Date().getMonth()} className="mt-1 block w-full bg-gray-50 border-gray-300 rounded-md shadow-sm p-3 font-semibold">
                            {Array.from({length: 12}, (_, i) => <option key={i} value={i}>{new Date(0,i).toLocaleString('en-US', {month: 'long'})}</option>)}
                        </select>
                    </div>
                     <div className="md:col-span-1">
                        <label htmlFor="year-select" className="block text-sm font-medium text-gray-600">Year</label>
                        <select id="year-select" defaultValue={new Date().getFullYear()} className="mt-1 block w-full bg-gray-50 border-gray-300 rounded-md shadow-sm p-3 font-semibold">
                             {Array.from({length: 5}, (_, i) => <option key={i} value={new Date().getFullYear() - i}>{new Date().getFullYear() - i}</option>)}
                        </select>
                    </div>
                    <button type="submit" disabled={isLoading} className="md:col-span-1 w-full bg-rose-600 text-white font-bold py-3 px-4 rounded-lg shadow-lg hover:bg-rose-700 transition-all disabled:bg-gray-400">
                        {isLoading ? "Generating..." : "Generate Report"}
                    </button>
                </form>
            </div>

            {reportData && (
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                    <div className="p-8 border-b" ref={reportRef}>
                         {/* --- START: CORRECTED JSX --- */}
                         {/* The parent div no longer has the 'no-print' class */}
                         <div className="flex justify-between items-center"> 
                            <div>
                                <h2 className="text-3xl font-bold text-gray-800">{reportData.employee.fullName}</h2>
                                <p className="text-gray-500">{reportData.period} Attendance Report</p>
                            </div>
                            {/* The class to hide is now ONLY on the button */}
                            <button onClick={handleExport} className="bg-green-600 text-white font-bold py-2 px-5 rounded-lg export-pdf-button">Export as PDF</button>
                        </div>
                        {/* --- END: CORRECTED JSX --- */}

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 text-center">
                             <div className="bg-green-100 p-4 rounded-lg"><p className="text-sm font-semibold text-green-700">Present</p><p className="text-3xl font-bold text-green-800">{reportData.summary.present}</p></div>
                             <div className="bg-red-100 p-4 rounded-lg"><p className="text-sm font-semibold text-red-700">Absent</p><p className="text-3xl font-bold text-red-800">{reportData.summary.absent}</p></div>
                             <div className="bg-yellow-100 p-4 rounded-lg"><p className="text-sm font-semibold text-yellow-700">Late</p><p className="text-3xl font-bold text-yellow-800">{reportData.summary.late}</p></div>
                             <div className="bg-blue-100 p-4 rounded-lg"><p className="text-sm font-semibold text-blue-700">On Leave</p><p className="text-3xl font-bold text-blue-800">{reportData.summary.onLeave}</p></div>
                        </div>
                         <div className="overflow-x-auto mt-6">
                            <table className="w-full text-sm text-left">
                                <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3">Date</th>
                                        <th className="px-6 py-3">Status</th>
                                        <th className="px-6 py-3">Check-in</th>
                                        <th className="px-6 py-3">Check-out</th>
                                        <th className="px-6 py-3">Location</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {reportData.logs.map(log => (
                                        <tr key={log.date}>
                                            <td className="px-6 py-4 font-medium text-gray-900">{log.date}</td>
                                            <td className="px-6 py-4">
                                                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusStyles(log.status)}`}>{log.status}</span>
                                            </td>
                                            <td className="px-6 py-4">{log.checkIn}</td>
                                            <td className="px-6 py-4">{log.checkOut}</td>
                                            <td className="px-6 py-4 text-xs">
                                                <div>{log.location.name}</div>
                                                {log.location.lat && log.location.lon && (
                                                    <div className="text-gray-500">({log.location.lat.toFixed(4)}, {log.location.lon.toFixed(4)})</div>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default MonthlyReportSection;
